<template>
  <component-wrapper
    :key="catalogId || 'loading'"
    class="product-detail-page-fi"
  >
    <product-detail-head-fi
      :catalog-data="getCatalogProductDataByIdData"
      :media-download-link="mediaDownloadLink"
      :fields="fields"
      :no-margin-bottom="Boolean(accessories?.length)"
      @added-to-memolist="addedToMemoList"
    />

    <product-items-slider-fi
      v-if="accessories?.length"
      :products="accessories"
      :slider-headline="$dict.get('product-Family.Accessories.Headline')"
      white
      expandable
    />

    <product-detail-accordions-fi :catalog-data="getCatalogProductDataByIdData || undefined" />

    <section-divider-small-fi />

    <cross-selling-product-teasers-fi
      v-if="
        fields.crossSellingProductTeaserEnabled?.value && getCatalogProductDataByIdData?.ProductId
      "
      :product-ids="getCatalogProductDataByIdData.ProductId"
    />

    <product-detail-schema-fi
      v-if="getCatalogProductDataByIdData"
      :catalog-data="getCatalogProductDataByIdData"
    />
  </component-wrapper>
</template>

<script setup lang="ts">
import { captureException } from '@sentry/browser';
import { useClientHandle, useQuery } from '@urql/vue';
import { computed, onServerPrefetch, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import CrossSellingProductTeasersFi from '@/components/cross-selling-product-teasers-fi/CrossSellingProductTeasersFi.vue';
import ProductDetailAccordionsFi from '@/components/product-detail/product-detail-accordions-fi/ProductDetailAccordionsFi.vue';
import ProductDetailHeadFi from '@/components/product-detail/product-detail-head-fi/ProductDetailHeadFi.vue';
import ProductDetailSchemaFi from '@/components/product-detail/product-detail-schema-fi/ProductDetailSchemaFi.vue';
import ProductItemsSliderFi from '@/components/product-items-slider-fi/ProductItemsSliderFi.vue';
import { useAppEvent } from '@/composables/appEvent';
import { useCatalog } from '@/composables/catalog';
import { useEventHub } from '@/composables/eventHub';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import {
  addToDataLayer,
  enableTrackingFunction,
  enableTrackingShopFunction,
  getCategories,
  getPriceForTrackingFunction,
  type TrackingEventItem,
} from '@/composables/tracking';
import type {
  GetBestPriceQuery,
  GetCatalogProductDataByIdQuery,
  GetCatalogProductDataByIdQueryVariables,
} from '@/generated/graphql';
import GET_CATALOG_PRODUCT_DATA_BY_ID from '@/graphql/getCatalogProductDataById.gql';
import SectionDividerSmallFi from '@/pages/section-divider-fi/SectionDividerSmallFi.vue';

defineOptions({ name: 'ProductDetailPageFi' });

const fields = useFields('ProductDetailPageFiFields');

const fischer = useFischer();
const { mediaDownloadLink, checkForMediaDownload } = useCatalog();
const { isPublicAssortment, getSalesChannels } = useSalesChannelFilter();
const { sendEvent } = useEventHub();
const handle = useClientHandle();
const { sendAppEvent } = useAppEvent();

const catalogId = computed(() => fischer.catalogId('040390'));

const enableTracking = () => enableTrackingFunction(fischer);
const enableTrackingShop = () => enableTrackingShopFunction(fischer);

const GET_BESTPRICE = () => import('@/graphql/getBestPrice.gql');

const getPriceForTracking = async (productIdUnpadded: string, qty: number = 0) => {
  const query = (await GET_BESTPRICE()).default;
  return getPriceForTrackingFunction(productIdUnpadded, qty, fischer, (variables: any) =>
    handle.client
      .query<GetBestPriceQuery>(query, variables, { requestPolicy: 'network-only' })
      .toPromise(),
  );
};

const trackViewItem = async (data: any, isFamily: boolean = false) => {
  try {
    if (!enableTracking()) return;

    const ecomLang = isFamily
      ? data.TextList.find((el: any) => el.Key === 'BEZEICHNUNG')?.Value
      : data.TextList.find((el: any) => el.Key === 'ECOMLANG')?.Value;
    const itemId = isFamily ? data.CategoryId : data.ProductId;

    const item: TrackingEventItem = {
      item_name: ecomLang,
      item_id: itemId,
      item_brand: fischer.brandName(),
      ...getCategories(data.CategoryDisplayNamePath),
    };

    if (!isFamily) {
      item.item_variant = data.ProductName;
    }

    const { price, quantity, currency, totalPrice } = await getPriceForTracking(data.ProductId);
    if (price) {
      item.price = price;
      item.quantity = quantity;
    }

    let ecommerce;
    if (totalPrice) {
      ecommerce = { currency, value: totalPrice };
    }

    addToDataLayer('view_item', [item], ecommerce);
  } catch (error) {
    console.error(error);
    captureException(error);
  }
};

const trackAddProductToWishlist = async (product: any) => {
  try {
    if (!(enableTracking() && enableTrackingShop())) return;

    const ecomLang = product.TextList.find((el: any) => el.Key === 'ECOMLANG')?.Value;

    const item: TrackingEventItem = {
      item_name: ecomLang,
      item_id: product.ProductId,
      item_brand: fischer.brandName(),
      item_variant: product.ProductName,
      ...getCategories(product.CategoryDisplayNamePath),
    };

    const { price, quantity, currency, totalPrice } = await getPriceForTracking(product.ProductId);
    if (price) {
      item.price = price;
      item.quantity = quantity;
    }

    let ecommerce;
    if (totalPrice) {
      ecommerce = { currency, value: totalPrice };
    }

    addToDataLayer('add_to_wishlist', [item], ecommerce);
  } catch (error) {
    console.error(error);
    captureException(error);
  }
};

const handleEventTracking = () => {
  if (IS_SSR) return;

  try {
    const recommendationClickDetails = localStorage.getItem('recommendationClick');
    if (recommendationClickDetails) {
      const { refererId, itemQuantity, guided } = JSON.parse(recommendationClickDetails);
      sendEvent(guided ? 'guided_recommendation_click' : 'recommendation_click', {
        url: window ? window.location.href : '',
        productId: catalogId.value,
        refererId,
        itemQuantity,
      });

      localStorage.removeItem('recommendationClick');
    }
  } catch (error) {
    console.debug('failed to process recommendation click', error);
  }
};

// R1: product catalog query — SEO-critical content, needs R0
const { data, executeQuery } = useQuery<
  GetCatalogProductDataByIdQuery,
  GetCatalogProductDataByIdQueryVariables
>({
  query: GET_CATALOG_PRODUCT_DATA_BY_ID,
  variables: computed(() => ({
    baseSiteId: fischer.baseSiteId(),
    apiLang: fischer.catalogLocale(),
    useSeries: fischer.isProductSeriesEnabled(),
    // NOTE: already gated in the pause condition
    id: catalogId.value ?? '',
    includeLogisticAttributes: fischer.shop.isClosedShop(),
    publicAssortment: isPublicAssortment(),
    salesChannels: getSalesChannels(),
  })),
  pause: computed(() => !catalogId.value),
});

// R0: product data is SEO-critical, must be available on first SSR paint
onServerPrefetch(async () => {
  if (!catalogId.value) return;
  await executeQuery();
});

const getCatalogProductDataByIdData = computed(() => data.value?.getCatalogProductDataById);

const accessories = computed(() =>
  getCatalogProductDataByIdData.value?.accessories?.filter((item) => item !== null),
);

watch(
  getCatalogProductDataByIdData,
  (d) => {
    if (d) {
      const subtitle = d.TextList?.find((el) => el?.Key === 'DOKBSTMARKCLAIM')?.Value;
      sendAppEvent('set-title', { title: d.ProductName, subtitle });
      trackViewItem(d);
    }
  },
  {
    immediate: true,
  },
);

const addedToMemoList = () => {
  if (getCatalogProductDataByIdData.value) {
    trackAddProductToWishlist(getCatalogProductDataByIdData.value);
  }
};

// created logic
if (catalogId.value) {
  checkForMediaDownload({ type: 'product', id: catalogId.value });
}
handleEventTracking();
</script>

<style scoped src="./product-detail-page-fi" />
